<template>
  <div
    v-if="card && card.type === 'Full'"
    class="card card--full w-full overflow-hidden relative"
  >
    <div
      class="
        card__zoom-bg
        bg-no-repeat bg-cover
        absolute
        w-full
        h-full
        transition-transform
        duration-100
        ease-in-out
      "
      :style="`background-image: url('${
        card.image ? card.image.fields.image.fields.file.url : ''
      }')`"
    ></div>
    <div
      class="
        absolute
        block
        h-auto
        bottom-0
        z-10
        text--all-white
        card__copy-wrap
        whitespace-normal
      "
    >
      <Markdown :source="card.content" />
      <div ref="copyWrap" class="mt-1">
        <div v-for="cta in card.cta">
          <Button
            v-if="cta.sys.contentType.sys.id === 'button'"
            :button="cta.fields"
          />
          <ArrowLink
            v-else-if="cta.sys.contentType.sys.id === 'arrowlink'"
            :arrow-link="cta.fields"
          />
        </div>
      </div>
    </div>
  </div>
  <div
    v-else-if="card && card.type === 'Simple Left Border'"
    class="card card--simple-border"
  >
    <LocaleLink
      v-if="card.link"
      :link="card.link"
      class="block pl-0 md:pl-1/2 underline"
    >
      <Markdown :source="card.content" />
    </LocaleLink>
  </div>
  <LocaleLink
    v-else-if="card.type === 'Icon Link' && card.link"
    :link="card.link"
    class="
      card card--icon
      cursor-pointer
      whitespace-normal
      no-underline
      flex flex-row
      items-center
      duration-200
      ease-in-out
      md:py-1/2
      md:pl-1/2
      md:pr-1
      p-0
      mt-1/4
      mb:mb-1/4
      mx-0
      mb-1/4
    "
  >
    <Icon v-if="card.image" :icon="card.image.fields" />
    <div class="text-wrap">
      <Markdown :source="card.content" />
      <svg
        width="14"
        height="11"
        viewBox="0 0 14 11"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        class="inline"
      >
        <path
          d="M6 1L11.1648 5.45055L6 9.9011"
          stroke="#00263E"
          stroke-width="2.5"
        />
        <rect y="4.25" width="11" height="2.5" fill="#00263E" />
      </svg>
    </div>
  </LocaleLink>
  <LogoCard
    v-else-if="card.type === 'Logo Link'"
    :card="card"
    class="card card--logo-link"
  />
  <ImageModal
    v-else-if="card.type === 'Image Modal'"
    :card="card"
    class="card card--image-modal"
  />
  <DefaultModalCard v-else-if="card.type === 'Default Modal'" :content="card">
    <div class="card__img-wrap">
      <Picture v-if="card.image" :image="card.image.fields" />
    </div>
    <div
      ref="copyWrap"
      class="card__copy-wrap flex flex-col pt-1 pb-2 pr-1 pl-1"
    >
      <Markdown :source="card.content" />
      <div v-for="cta in card.cta" :key="cta">
        <Button
          v-if="cta.sys.contentType.sys.id === 'button'"
          :button="cta.fields"
        />
        <ArrowLink
          v-else-if="cta.sys.contentType.sys.id === 'arrowlink'"
          :arrow-link="cta.fields"
        />
      </div>
    </div>
  </DefaultModalCard>
  <div
    v-else-if="card.type === 'White Rounded'"
    class="card--white-rounded pt-2 pb-2 rounded-md bg-white p-0 h-full"
  >
    <div class="card__img-wrap w-auto text-center">
      <Picture v-if="card.image" :image="card.image.fields" />
    </div>
    <div class="card__copy-wrap text-blue-dark">
      <Markdown :source="card.content" />
    </div>
  </div>
  <div
    v-else-if="card || card.type === 'Blank'"
    class="card flex flex-col shadow-lg bg-white relative"
  >
    <div
      class="card__img-wrap"
      :class="{ 'card__img-wrap-blank pl-2 pr-2 pt-2': card.type === 'Blank' }"
    >
      <Picture v-if="card.image" :image="card.image.fields" />
    </div>
    <div
      ref="copyWrap"
      class="card__copy-wrap flex flex-col pt-1 pb-2"
      :class="card.type === 'Blank' ? 'pl-2 pr-2' : 'pr-1 pl-1'"
    >
      <Markdown :source="card.content" />
      <div v-for="cta in card.cta">
        <Button
          v-if="cta.sys.contentType.sys.id === 'button'"
          :button="cta.fields"
        />
        <ArrowLink
          v-else-if="cta.sys.contentType.sys.id === 'arrowlink'"
          :arrow-link="cta.fields"
        />
      </div>
    </div>
  </div>
  <div v-else></div>
</template>
<style lang="scss">
.card--white-rounded {
  .card__copy-wrap {
    h5,
    p {
      color: theme('colors.blue.dark') !important;
    }

    h5 {
      @apply pt-1 text-center;
    }

    p {
      margin-bottom: 0;
    }

    ul {
      @apply pl-0 m-0 list-none;

      li {
        @apply mb-1/4 pl-1 pr-1/2;

        font-size: 15px;

        &::before {
          content: '• ';
          color: theme('colors.blue.default');
          margin-left: -8px;
        }
      }
    }

    @media (max-width: theme('screens.md')) {
      ul {
        text-align: center;

        li {
          padding-left: 0;
        }
      }
    }
  }
}

.card--icon {
  .text-wrap > div {
    display: inline;
  }

  .icon {
    @apply mb-0 mr-1/2 h-auto mt-0;

    max-width: 40px;
    max-height: 40px;

    @media (max-width: theme('screens.md')) {
      @apply hidden;
    }
  }

  h5 {
    @apply text-blue-dark mb-0 inline;
  }

  p {
    @apply mb-0;
  }

  em {
    @apply text-blue not-italic;
    @media (min-width: theme('screens.md')) {
      @apply hidden;
    }
  }

  svg {
    width: 12px;
    margin-left: 4px;

    rect {
      opacity: 0;
      transition: 0.1s ease-in-out;
    }

    path {
      transform: translateX(-5px);
      transition: 0.1s ease-in-out;
    }
  }

  &:hover {
    svg {
      rect {
        opacity: 1;
        fill: theme('colors.blue.default');
      }

      path {
        transform: translateX(0);
        stroke: theme('colors.blue.default');
      }
    }

    h5 {
      color: theme('colors.blue.default');
    }

    @media (max-width: theme('screens.md')) {
      box-shadow: none;
    }

    .icon {
      filter: invert(53%) sepia(69%) saturate(6933%) hue-rotate(194deg)
        brightness(102%) contrast(98%);
    }
  }
}

.card--simple-border {
  border-bottom: 1px solid rgba(#00263e, 0.12); // can't grab from theme due to scss

  h4 {
    @apply text-lg mb-2 leading-7;

    text-decoration-color: theme('colors.blue.dark');
  }

  @media (min-width: theme('screens.md')) {
    min-height: 150px;
    border-left: 1px solid rgba(#00263e, 0.12);
    border-bottom: 0;

    h4 {
      @apply mb-0;
    }
  }
}
.card--full {
  padding-bottom: 98%;
  width: calc(100% + 22px);
  &::after {
    content: '';
    @apply absolute w-full h-full left-0;

    background: linear-gradient(
      358.15deg,
      rgba(0, 0, 0, 0.85) 13.66%,
      rgba(0, 38, 62, 0) 77.89%
    );
  }

  &:hover {
    .card__zoom-bg {
      transform: scale(1.1);
    }
  }

  @media (min-width: 1680px) {
    height: 711px;
    @apply pb-0;
  }

  .card__copy-wrap {
    max-width: 550px;
    padding: 0 0 85px 85px;

    @media (max-width: theme('screens.lg')) {
      padding: 0 22px 22px 22px;
    }
  }
  @media (max-width: theme('screens.md')) {
    margin-bottom: -20px;
  }
}
.card__img-wrap {
  img {
    @apply w-full h-auto;
  }
}
.card__img-wrap-blank {
  img {
    @apply w-auto;
  }
}

.card__copy-wrap {
  h2 {
    @apply mb-0;
  }
}
</style>
<script>
import Picture from './Picture';

export default {
  components: {
    Picture,
  },
  props: ['card'],
};
</script>
